import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ME_KEY, type Profile } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

const schema = z.object({
  name: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
});

/** Имя. Выбора языка нет: интерфейс только на английском (ADR-0021). */
export function GeneralSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    values: { name: profile.name },
  });
  const update = useMutation({
    mutationFn: async (body: { name: string }) => unwrap(await api.PATCH('/api/v1/me', { body })),
    onSuccess: (updated) => {
      queryClient.setQueryData(ME_KEY, updated);
    },
  });

  return (
    <Card title={t('profile.general.title')}>
      <form
        className="flex items-end gap-3"
        onSubmit={form.handleSubmit((values) => update.mutate({ name: values.name }))}
        noValidate
      >
        <div className="flex-1">
          <TextField
            control={form.control}
            name="name"
            label={t('auth.field.name')}
            autoComplete="name"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={update.isPending}>
          {t('common.save')}
        </Button>
      </form>
      {update.isError && <Notice tone="danger">{t(errorMessageKey(update.error))}</Notice>}
    </Card>
  );
}
